import React, { createContext, useContext, useState, useEffect } from "react"
import locales from "../locales.json"

/**
 * Loads the translation file for a given language code.
 * Falls back to default language if not found.
 * @param {string} languageCode - The language code (e.g. 'fr', 'en')
 * @returns {Promise<Object>} The translation object
 */
const loadTranslation = async languageCode => {
  try {
    const translation = await import(`../baseTranslation/${languageCode}.json`)
    return translation.default
  } catch (error) {
    console.warn(`Translation file for ${languageCode} not found, falling back to default`)
    const defaultTranslation = await import(`../baseTranslation/${locales.default}.json`)
    return defaultTranslation.default
  }
}

const LanguageContext = createContext()

/**
 * Custom hook to access the language context.
 * Throws if used outside LanguageProvider.
 * @returns {Object} Language context value
 */
export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}

/**
 * Provides language state, translation loading, and translation function to children.
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(locales.default)
  const [translations, setTranslations] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadTranslations = async () => {
      if (!currentLanguage) return

      setLoading(true)
      try {
        const translationData = await loadTranslation(currentLanguage)
        setTranslations(translationData || {})
      } catch (error) {
        console.error("Error loading translations:", error)
        setTranslations({})
      } finally {
        setLoading(false)
      }
    }
    loadTranslations()
  }, [currentLanguage])

  const changeLanguage = languageCode => {
    if (languageCode !== null && languageCode !== undefined) {
      setCurrentLanguage(languageCode)
    }
  }

  /**
   * Translates a key using the loaded translations.
   * Supports nested keys with dot notation.
   * @param {string} key
   * @returns {string}
   */
  const t = key => {
    if (loading) return key
    if (!key || typeof key !== "string") return key
    if (!translations || typeof translations !== "object") return key

    const keys = key.split(".")
    let value = translations
    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = value[k]
      } else {
        return key
      }
    }
    return value || key
  }

  const value = {
    currentLanguage,
    changeLanguage,
    translations,
    loading,
    t,
    availableLanguages: locales.languages,
  }

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}
